
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { OrganizationSwitcher } from "@/components/OrganizationSwitcher";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { signOutUser } from "@/lib/auth";
import { LogOut, Menu, Shield, User } from "lucide-react";
import React from "react";

interface HeaderProps {
  toggleSidebar: () => void;
}

const Header: React.FC<HeaderProps> = ({ toggleSidebar }) => {
  const { user, selectedOrganization, isSuperAdmin } = useAuth();
  const { toast } = useToast();

  const handleSignOut = async () => {
    try {
      await signOutUser();
      toast({
        title: "Signed out",
        description: "You have been successfully signed out.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <header className="bg-background border-b border-border py-2 px-4 flex items-center justify-between">
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleSidebar}
        className="text-foreground md:hidden">
        <Menu className="h-6 w-6" />
      </Button>

      {/* min-w-0 + truncate let the title give way on narrow screens so the picker and
          account menu stay on-screen. */}
      <div className="flex min-w-0 flex-1 items-center justify-between gap-2 md:ml-64">
        <div className="flex min-w-0 items-center gap-2">
          <img
            src="/lovable-uploads/9f53686e-e465-41d4-a5aa-241a2e509798.png"
            alt="GLEAN Logo"
            className="h-8 w-8 object-contain rounded-lg"
          />
          <div className="min-w-0 truncate text-xl font-semibold text-agri-green">
            {selectedOrganization?.name || "My"} Field Trials
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-4">
          {/* Super Admin Badge */}
          {isSuperAdmin && (
            <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded-md text-sm font-medium">
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Super Admin</span>
            </div>
          )}

          {/* Organization switcher (any user who can reach more than one org) */}
          <OrganizationSwitcher />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full bg-agri-gray-light text-agri-green-dark hover:bg-agri-gray-light/90">
                <User className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{user?.displayName || user?.email || "User"}</p>
                  <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleSignOut}
                className="cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Header;
